import {
  Box,
  Grid,
  MenuItem,
  TextField,
} from '@mui/material';
import { Dispatch, SetStateAction } from 'react';
import { useSelector } from 'react-redux';
import type { IGender } from '../Interfaces/IGender';
import type { ILegalClientFullObject } from '../Interfaces/ILegalClient';
import type { State } from '../redux/mainReducer';
import { useGetLegalClientGroups } from '../hooks/reactQuerys/LegalClientGroups';
import { LegalClientGroupSelector } from '../LegalClientGroup/LegalClientGroupSelector';

interface IProps {
  orgObject?: ILegalClientFullObject;
  formValues: ILegalClientFullObject;
  setFormValues: Dispatch<SetStateAction<ILegalClientFullObject>>;
}

export const NaturalPersonForm = ({
  orgObject,
  formValues,
  setFormValues,
}: IProps) => {
  const genders = useSelector((state: State) => state.genderArray.storeGender);
  const academicTitles = useSelector(
    (state: State) => state.academicTitleArray.storeAcademicTitle
  );

  const { legalClientGroups } = useGetLegalClientGroups();

  const countries = useSelector(
    (state: State) => state.countryArray.storeCountry
  );

  return (
    <>
      <Grid item xs={3}>
        <LegalClientGroupSelector
          value={formValues.LegalClientGroups}
          options={legalClientGroups}
          onChange={(newValue) =>
            setFormValues(prev => ({ ...prev, LegalClientGroups: newValue }))
          }
        />
      </Grid>

      <Grid item xs={6} display='flex' justifyContent='flex-end'>
        {formValues.idArnoTopIntern && (
          <Box>
            <strong>Namen.Nr.:</strong> {formValues.idArnoTopIntern}
          </Box>
        )}
      </Grid>
      <Grid item xs={3}>
        <TextField
          select
          required
          value={formValues.idGender || ''}
          label='Geschlecht'
          size='small'
          fullWidth
          onChange={event => {
            const genderId = Number(event.target.value);

            setFormValues((prev: ILegalClientFullObject) => ({
              ...prev,
              idGender: genderId,
              Gender: genders.find(gender => gender.idGender === genderId)
                ?.Gender,
            }));
          }}
        >
          {genders.map((gender: IGender, i) => (
            <MenuItem key={i} value={gender.idGender}>
              {gender.Gender}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      <Grid item xs={3}>
        <TextField
          select
          label='Akademischer Titel'
          size='small'
          fullWidth
          value={formValues.idAcademicTitle || ''}
          onChange={event => {
            const academicTitleId = Number(event.target.value);
            setFormValues((prev: ILegalClientFullObject) => ({
              ...prev,
              idAcademicTitle: academicTitleId,
              AcademicTitle: academicTitles.find(
                title => title.idAcademicTitle === academicTitleId
              )?.AcademicTitle,
            }));
          }}
        >
          {academicTitles.map((title, i) => (
            <MenuItem key={i} value={title.idAcademicTitle}>
              {title.AcademicTitle}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      <Grid item xs={3}>
        <TextField
          required
          value={formValues.FirstName || ''}
          label='Vorname'
          size='small'
          fullWidth
          onChange={event => {
            setFormValues((prev: ILegalClientFullObject) => ({
              ...prev,
              FirstName: event.target.value,
            }));
          }}
        />
      </Grid>
      <Grid item xs={3}>
        <TextField
          required
          value={formValues.LastName || ''}
          label='Nachname'
          size='small'
          fullWidth
          onChange={event => {
            setFormValues((prev: ILegalClientFullObject) => ({
              ...prev,
              LastName: event.target.value,
            }));
          }}
        />
      </Grid>

      <Grid item xs={12}>
        <Box sx={{ mt: 0.5 }} />
      </Grid>
      <Grid item xs={4}>
        <TextField
          InputLabelProps={{ shrink: true }}
          label='Geburtstag'
          size='small'
          value={formValues.Birthdate || ''}
          fullWidth
          type='date'
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            setFormValues((prev: ILegalClientFullObject) => ({
              ...prev,
              Birthdate: event.target.value,
            }))
          }
        />
      </Grid>

      <Grid item xs={4}>
        <TextField
          label='Geburtsort'
          size='small'
          value={formValues.Birthplace || ''}
          fullWidth
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            setFormValues((prev: ILegalClientFullObject) => ({
              ...prev,
              Birthplace: event.target.value,
            }))
          }
        />
      </Grid>
      <Grid item xs={4}>
        <TextField
          label='Nationalität'
          size='small'
          value={formValues.idCountry || ''}
          fullWidth
          select
          onChange={event =>
            setFormValues((prev: ILegalClientFullObject) => ({
              ...prev,
              idCountry: Number(event.target.value),
            }))
          }
        >
          {countries.map((c, i) => (
            <MenuItem key={i} value={c.idCountry}>
              {c.Nationality}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      <Grid item xs={6}>
        <TextField
          InputLabelProps={{ shrink: true }}
          label='Sterbedatum'
          size='small'
          value={formValues.DateOfDeath || ''}
          fullWidth
          type='date'
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            setFormValues((prev: ILegalClientFullObject) => ({
              ...prev,
              DateOfDeath: event.target.value,
            }))
          }
        />
      </Grid>
      <Grid item xs={6}>
        <TextField
          label='Sterbeort'
          size='small'
          value={formValues.PlaceOfDeath || ''}
          fullWidth
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            setFormValues((prev: ILegalClientFullObject) => ({
              ...prev,
              PlaceOfDeath: event.target.value,
            }))
          }
        />
      </Grid>

      <Grid item xs={12}>
        <Box sx={{ mt: 0.5 }} />
      </Grid>

      <Grid item xs={3}>
        <TextField
          label='Steuerliche Identifikationsnummer'
          size='small'
          value={formValues.TaxNumber || ''}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            setFormValues((prev: ILegalClientFullObject) => ({
              ...prev,
              TaxNumber: event.target.value,
            }))
          }
          fullWidth
        />
      </Grid>
    </>
  );
};
